import { useCallback, useState } from 'react';

export type ViewMode = 'table' | 'feed';
const STORAGE_KEY = 'trilirivos-moderation-view';

export function useViewMode(): [ViewMode, (m: ViewMode) => void] {
  const [mode, setModeState] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'table';
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw === 'feed' ? 'feed' : 'table';
    } catch {
      // Storage disabled / SecurityError (Safari private, sandboxed iframe, etc.) — fall back.
      return 'table';
    }
  });

  const setMode = useCallback((next: ViewMode) => {
    setModeState(next);
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence is non-critical — swallow quota/security errors.
    }
  }, []);

  return [mode, setMode];
}

interface Props {
  mode: ViewMode;
  onChange: (next: ViewMode) => void;
}

export default function ViewToggle({ mode, onChange }: Props) {
  return (
    <div className="Moderation__ViewToggle" role="group" aria-label="View mode">
      <button
        type="button"
        aria-label="Table view"
        aria-pressed={mode === 'table'}
        onClick={() => onChange('table')}
      >
        Table
      </button>
      <button
        type="button"
        aria-label="Feed view"
        aria-pressed={mode === 'feed'}
        onClick={() => onChange('feed')}
      >
        Feed
      </button>
    </div>
  );
}
